/**
 * SanityCheckCard — Final Sanity Check surface.
 *
 * Three rendering states keyed off the findings array:
 *
 *   1. Empty (`findings.length === 0`)         → renders nothing.
 *   2. All resolved + collapsed (default)      → one-line strip.
 *   3. Has unresolved OR user expanded         → full card.
 *
 * The all-resolved-collapsed strip is the everyday state once the
 * user has reviewed each finding — most reports land here, and the
 * verbose tile column was visual noise that made completed QA work
 * look like an open task list. The "View" toggle on the strip
 * expands the card inline so audit trails + the "Undo" affordance per
 * finding remain reachable.
 *
 * Apply-on-Rerun pack (Section D): the per-finding action row is two
 * buttons — "Apply on rerun" / "Ignore" — plus an "Edit suggestion"
 * toggle that opens an inline textarea so the user can revise the
 * AI's suggestion before queuing it. "Apply" snapshots an
 * `applyDirective` (the edited text or the original suggestion + the
 * finding's evidence + section) that the next rerun's narrative
 * builders incorporate; "Ignore" only suppresses re-flagging.
 *
 * State machine is purely local — toggling the strip's "View" just
 * flips an internal expanded flag; per-finding `editing` / draft text
 * is keyed by finding id. The persisted resolutions on
 * `report.sanityFindings` are owned by the parent (`resolveSanity`).
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Pencil, XCircle } from 'lucide-react';
import type { ApplyDirective, SanityFinding, SiteRagReport } from '../../types/siteRag';
import {
  isDiagnosticComplaintNotReplacement,
  listCandidateFieldPathsForFinding,
} from '../../lib/reportGeneration/applySourceSupportedAuditCorrection';
import { CountReconciliationInfo } from './CountReconciliationInfo';

/** What the per-finding action row hands back to the parent. */
export interface SanityResolutionChange {
  /** `'apply' | 'ignore'`, or `undefined` when the user hit Undo. */
  resolution: SanityFinding['resolution'];
  /** The edited suggestion text — present only when the user edited
   *  before applying. Undo / Ignore pass it as undefined. */
  userNote?: string;
  /** The directive snapshot — present only when `resolution === 'apply'`. */
  applyDirective?: ApplyDirective;
}

/** Input payload for applying a source-supported correction immediately without a full AI rerun. */
export interface SanityApplyNowInput {
  replacementText: string;
  evidenceCitation: string;
  fieldPath?: string;
  targetSubstring?: string;
  userNote?: string;
  applyDirective?: ApplyDirective;
}

/** ITT-1 — Taxonomy filter bucket. `'all'` reverts to the legacy
 *  unfiltered render so callers that haven't migrated still work. */
export type FindingsFilter = 'all' | 'issues' | 'verified' | 'other';

/**
 * ITT-1/ITT-2 — Category taxonomy. `issues` are the action-required
 * categories (real contradictions / overstatements / hallucinations /
 * leaked instructions). `verified` are positive verifications — QA
 * confirming the report's claims are accurate; no action needed beyond
 * acknowledgement. Anything else (e.g. `'unverified'`, `'citations'`,
 * `'document-conflict'`, or a legacy/unknown category) defaults to the
 * `other` bucket so the segmentation degrades gracefully — see Risk R5
 * on the prompt pack.
 */
const ISSUE_CATEGORIES: ReadonlySet<SanityFinding['category']> = new Set([
  'inconsistency',
  'misleading',
  'false-positive',
]);
const VERIFIED_CATEGORIES: ReadonlySet<SanityFinding['category']> = new Set([
  'risk-verified',
  'strength-verified',
]);

/** Bucket a single finding for ITT-1 taxonomy + ITT-2 adaptive actions. */
export function findingBucket(finding: SanityFinding): 'issues' | 'verified' | 'other' {
  if (ISSUE_CATEGORIES.has(finding.category)) return 'issues';
  if (VERIFIED_CATEGORIES.has(finding.category)) return 'verified';
  return 'other';
}

/** ITT-2 — verified findings drop "Apply on rerun" + "Ignore" in
 *  favour of a single "Acknowledge" affordance; "Edit suggestion"
 *  stays so the user can still revise the AI's note. */
export function isVerifiedFinding(finding: SanityFinding): boolean {
  return VERIFIED_CATEGORIES.has(finding.category);
}

interface Props {
  findings: SanityFinding[];
  /**
   * Fires when the user clicks Apply on rerun / Ignore / Undo on a
   * finding. The parent owns persistence (writes back to
   * `report.sanityFindings` via the store) + the audit event.
   */
  onResolve: (
    finding: SanityFinding,
    idx: number,
    next: SanityResolutionChange,
  ) => void;
  /**
   * Optional handler that applies a source-supported factual correction
   * immediately to a new immutable report revision without a full AI rerun.
   */
  onApplyNow?: (
    finding: SanityFinding,
    idx: number,
    input: SanityApplyNowInput,
  ) => Promise<{ applied: boolean; error?: string; reason?: string } | void> | void;
  /**
   * Force the card open regardless of the all-resolved state. Used
   * when the parent wants the appendix to start expanded (e.g. when
   * an unresolved-pill click anchors here). Defaults to undefined,
   * which lets the internal state machine drive the open/closed
   * decision.
   */
  forceExpanded?: boolean;
  /**
   * ITT-1/ITT-4 — initial taxonomy filter. Used when the parent opens
   * the card from a cross-link (e.g. "4 issues flagged →" on the
   * Quality tab pre-filters to the issue bucket). Defaults to `'all'`.
   * Changing this prop re-seeds the local filter state so re-opens
   * land on the right bucket; the user can still toggle filters
   * manually after that.
   */
  initialFilter?: FindingsFilter;
  /** Optional report record used to offer target field path chips and current field text loading. */
  report?: SiteRagReport;
}

/** Build the directive a user is queuing — the edited text if they
 *  edited it, else the AI's original suggestion. */
function buildDirective(finding: SanityFinding, editedText?: string): ApplyDirective {
  const fallback =
    finding.userNote ?? finding.applyDirective?.suggestionAtResolution ?? finding.suggestion;
  const text = editedText && editedText.trim().length > 0 ? editedText.trim() : fallback;
  const d: ApplyDirective = { suggestionAtResolution: text, section: finding.section };
  if (finding.evidence) d.evidenceAtResolution = finding.evidence;
  return d;
}

export function SanityCheckCard({ findings, onResolve, onApplyNow, forceExpanded, initialFilter, report }: Props) {
  // Pre-derived counts — used by the strip header, the require-attention
  // count in the full card, and the all-resolved branch decision.
  const unresolved = useMemo(() => findings.filter(f => !f.resolution), [findings]);
  const hasQueuedApplyNow = useMemo(
    () => Boolean(onApplyNow && findings.some(f => f.resolution === 'apply')),
    [findings, onApplyNow],
  );
  const allResolved = findings.length > 0 && unresolved.length === 0 && !hasQueuedApplyNow;

  // ITT-1 — Pre-bucketed counts for the taxonomy header. We bucket the
  // full findings array (not just unresolved) so the banner reads the
  // same after a user has actioned a finding — otherwise "5 issues"
  // would silently drop to "4 issues" the moment they hit Apply,
  // hiding work-in-progress. Filter state controls which subset
  // renders below; default `'all'` matches the legacy unfiltered
  // behaviour and is the only state that exists on first mount.
  const bucketed = useMemo(() => {
    const issues: SanityFinding[] = [];
    const verified: SanityFinding[] = [];
    const other: SanityFinding[] = [];
    for (const f of findings) {
      const b = findingBucket(f);
      if (b === 'issues') issues.push(f);
      else if (b === 'verified') verified.push(f);
      else other.push(f);
    }
    return { issues, verified, other };
  }, [findings]);
  const [filter, setFilter] = useState<FindingsFilter>(initialFilter ?? 'all');
  // Re-seed the filter when the parent changes `initialFilter` — that
  // signals a fresh cross-link (e.g. user clicked "issues flagged →"
  // on the Quality tab and the tray bounced them here pre-filtered).
  // The user can still toggle filters manually after that.
  React.useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);
  const visibleFindings = useMemo(() => {
    if (filter === 'issues') return bucketed.issues;
    if (filter === 'verified') return bucketed.verified;
    if (filter === 'other') return bucketed.other;
    return findings;
  }, [filter, bucketed, findings]);

  // Locally-driven expansion. Default state when all resolved is
  // collapsed (the strip); when there are unresolved findings the
  // card stays open so the user can act on them.
  const [expanded, setExpanded] = useState<boolean>(!allResolved);

  // Per-finding inline-edit state, keyed by finding id. `editing` is
  // the set of ids whose textarea is open; `drafts` is the working text.
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [citationDrafts, setCitationDrafts] = useState<Record<string, string>>({});
  const [fieldPathDrafts, setFieldPathDrafts] = useState<Record<string, string>>({});
  const [targetSubstringDrafts, setTargetSubstringDrafts] = useState<Record<string, string>>({});
  const [applyingNow, setApplyingNow] = useState<Record<string, boolean>>({});
  const [applyNowErrors, setApplyNowErrors] = useState<Record<string, string>>({});

  // The forceExpanded prop wins over local state when set — useful
  // for the appendix surface where an external pill-click should
  // open the card. Computed via a ternary so toggling expanded
  // locally still works once the prop reverts to undefined.
  const isOpen = typeof forceExpanded === 'boolean' ? forceExpanded : expanded;

  // Empty: nothing to surface.
  if (findings.length === 0) return null;

  // All resolved + closed: thin one-line strip.
  if (allResolved && !isOpen) {
    return (
      <div
        className="flex items-center justify-between text-xs text-text-secondary py-2 px-3"
        data-testid="sanity-check-strip"
        data-mode="collapsed"
      >
        <span className="flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-success" aria-hidden="true" />
          Final sanity check — {findings.length} finding{findings.length === 1 ? '' : 's'} reviewed and resolved
        </span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="type-meta text-primary hover:text-primary/80 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          data-testid="sanity-check-view"
          aria-expanded="false"
          aria-controls="sanity-check-card-body"
        >
          View
        </button>
      </div>
    );
  }

  // Full card.
  const issueCount = bucketed.issues.length;
  const verifiedCount = bucketed.verified.length;
  const otherCount = bucketed.other.length;
  // ITT-1 — Banner reads "X issues · Y verified" not "N require attention"
  // when at least one finding has a known taxonomy bucket. For purely
  // "other" / legacy reports we fall back to the original count copy
  // so we never overstate "0 issues" when the schema is unknown (R5).
  const hasTaxonomy = issueCount > 0 || verifiedCount > 0;
  return (
    <div
      className="border border-border bg-state-hover p-3 space-y-3"
      data-testid="sanity-check-card"
      data-mode={allResolved ? 'expanded-resolved' : 'expanded-pending'}
      id="sanity-check-card-body"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-widest text-warning flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" aria-hidden="true" />
          {hasTaxonomy
            ? 'Final Sanity Check'
            : allResolved
              ? `Final Sanity Check — ${findings.length} finding${findings.length === 1 ? '' : 's'} reviewed`
              : `Final Sanity Check — ${unresolved.length} finding${unresolved.length === 1 ? '' : 's'} require attention`}
          {/* ITT-4 — disclose the relationship between this count and the
               two other count surfaces (Site Evidence conflicts +
               Quality issues flagged) so users stop trying to reconcile
               three different numbers in their head. */}
          <CountReconciliationInfo surface="findings" />
        </h3>
        <div className="flex items-center gap-2">
          {allResolved && (
            <span className="text-xs font-bold text-success uppercase tracking-widest">All Resolved</span>
          )}
          {/* Hide control mirrors the strip's View affordance — keeps
              the user's mental model consistent. Suppressed when
              forceExpanded is on (the parent owns the visibility). */}
          {allResolved && typeof forceExpanded !== 'boolean' && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="type-meta text-text-muted hover:text-text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              data-testid="sanity-check-hide"
              aria-expanded="true"
              aria-controls="sanity-check-card-body"
            >
              Hide
            </button>
          )}
        </div>
      </div>
      {/* ITT-1 — Taxonomy split header. Replaces the monolithic
           "18 findings require attention" with clickable filter chips
           so the user can see at-a-glance how many findings actually
           need action ("issues") versus positive verifications that
           just confirm accuracy ("verified"). Suppressed when the
           taxonomy is empty (legacy reports) — the legacy header
           above carries the count instead. */}
      {hasTaxonomy && (
        <div
          className="flex flex-wrap items-center gap-2 text-xs"
          data-testid="findings-taxonomy-banner"
        >
          <button
            type="button"
            onClick={() => setFilter('all')}
            aria-pressed={filter === 'all'}
            data-testid="findings-count-all"
            className={`px-2 py-0.5 border rounded-md uppercase tracking-widest text-[10px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
              filter === 'all'
                ? 'selected-accent'
                : 'border-border text-text-muted hover:text-text-primary'
            }`}
          >
            All {findings.length}
          </button>
          <button
            type="button"
            onClick={() => setFilter('issues')}
            aria-pressed={filter === 'issues'}
            data-testid="findings-count-issues"
            className={`px-2 py-0.5 border rounded-md uppercase tracking-widest text-[10px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning focus-visible:ring-offset-1 ${
              filter === 'issues'
                ? 'border-warning bg-warning/15 text-warning'
                : 'border-warning/40 text-warning hover:bg-warning/10'
            }`}
          >
            ⚠ {issueCount} issue{issueCount === 1 ? '' : 's'}
          </button>
          <button
            type="button"
            onClick={() => setFilter('verified')}
            aria-pressed={filter === 'verified'}
            data-testid="findings-count-verified"
            className={`px-2 py-0.5 border rounded-md uppercase tracking-widest text-[10px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-1 ${
              filter === 'verified'
                ? 'border-success bg-success/15 text-success'
                : 'border-success/40 text-success hover:bg-success/10'
            }`}
          >
            ✓ {verifiedCount} verified
          </button>
          {otherCount > 0 && (
            <button
              type="button"
              onClick={() => setFilter('other')}
              aria-pressed={filter === 'other'}
              data-testid="findings-count-other"
              className={`px-2 py-0.5 border rounded-md uppercase tracking-widest text-[10px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
                filter === 'other'
                  ? 'selected-accent'
                  : 'border-border text-text-muted hover:text-text-primary'
              }`}
            >
              · {otherCount} other
            </button>
          )}
        </div>
      )}
      {/* No inner max-height / scroll: when this card lives in the
          Insights tray (its only mount today) the tray's own tab
          content owns the vertical scroll, so a tall findings list
          stays fully visible instead of being clipped. */}
      {hasTaxonomy && visibleFindings.length === 0 && (
        <p
          className="text-[11px] text-text-muted"
          data-testid="findings-filter-empty"
        >
          No findings in this bucket. Pick a different filter above.
        </p>
      )}
      <div className="space-y-2">
        {visibleFindings.map((finding) => {
          const idx = findings.indexOf(finding);
          const isEditing = !!editing[finding.id];
          const rawQueuedText = finding.userNote ?? finding.applyDirective?.suggestionAtResolution ?? finding.suggestion;
          const isQueuedComplaint =
            !finding.userNote &&
            isDiagnosticComplaintNotReplacement(
              rawQueuedText ?? '',
              [finding.finding ?? ''].filter(Boolean),
            ).isComplaint;
          const queuedText = isQueuedComplaint ? '' : rawQueuedText;
          const candidateFields = report ? listCandidateFieldPathsForFinding(report, finding) : [];
          const selectedCandidateField =
            candidateFields.find((cf) => cf.path === (fieldPathDrafts[finding.id]?.trim() || '')) ??
            candidateFields[0];
          const draft =
            drafts[finding.id] ??
            (isQueuedComplaint && selectedCandidateField ? selectedCandidateField.currentText : queuedText);
          return (
            <div
              key={finding.id}
              className={`border p-3 space-y-2 ${
                // Apply-on-Rerun queue tint — switched from cyan-500 to the
                // semantic `info` token so the panel reads cleanly in both
                // light and dark mode (cyan-300 text on a light card was
                // washed out). Mirrors RerunFreshnessList's queued-apply card.
                finding.resolution === 'apply' ? 'border-info/40 bg-info/5' :
                finding.resolution === 'ignore' ? 'border-border bg-muted opacity-60' :
                finding.severity === 'high' ? 'border-danger/40 bg-danger/15' :
                'border-warning/40 bg-card'
              }`}
              data-testid={`sanity-finding-${finding.id}`}
              data-resolution={finding.resolution ?? 'unresolved'}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 ${
                      finding.category === 'risk-verified' ? 'bg-danger/15 text-danger' :
                      finding.category === 'strength-verified' ? 'bg-success/15 text-success' :
                      finding.category === 'inconsistency' ? 'bg-danger/15 text-danger' :
                      finding.category === 'document-conflict' ? 'bg-purple-500/15 text-purple-400' :
                      finding.category === 'false-positive' ? 'bg-warning/15 text-warning' :
                      finding.category === 'misleading' ? 'bg-warning/15 text-warning' :
                      'bg-surface-3 text-text-muted'
                    }`}>{finding.category.replace(/-/g, ' ')}</span>
                    {finding.verificationStatus && finding.verificationStatus !== 'not-applicable' && (
                      <span className={`text-xs font-bold uppercase px-1.5 py-0.5 ${
                        finding.verificationStatus === 'confirmed' ? 'bg-success/15 text-success' :
                        finding.verificationStatus === 'partially-confirmed' ? 'bg-warning/15 text-warning' :
                        'bg-danger/15 text-danger'
                      }`}>{finding.verificationStatus.replace('-', ' ')}</span>
                    )}
                    <span className="text-xs text-text-secondary">{finding.section}</span>
                    <span className={`text-xs font-bold uppercase ${finding.severity === 'high' ? 'text-danger' : finding.severity === 'medium' ? 'text-warning' : 'text-text-secondary'}`}>{finding.severity}</span>
                    {finding.resolution === 'apply' && (
                      <span
                        className="type-section px-1.5 py-0.5 bg-info/15 text-info"
                        data-testid={`sanity-finding-queued-${finding.id}`}
                      >
                        Queued
                      </span>
                    )}
                    {!finding.resolution && finding.correctionLineage?.status === 'applied-still-failing' && (
                      <span
                        className="type-section px-1.5 py-0.5 bg-warning/15 text-warning"
                        data-testid={`sanity-finding-applied-still-failing-${finding.id}`}
                      >
                        Applied on rerun — still flagged
                      </span>
                    )}
                    {finding.verdictConflict?.status === 'conflict-with-canonical-conclusion' && (
                      <span
                        className="type-section px-1.5 py-0.5 bg-warning/15 text-warning"
                        data-testid={`sanity-finding-verdict-conflict-badge-${finding.id}`}
                      >
                        Verdict conflict ({finding.verdictConflict.historicalStatedVerdict} vs {finding.verdictConflict.canonicalConclusionVerdict})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{finding.finding}</p>
                  {finding.evidence && (
                    <div className="text-xs text-muted-foreground mt-1 bg-muted p-1.5 border-l-2 border-border">
                      <span className="font-bold text-text-secondary">Evidence:</span> {finding.evidence}
                    </div>
                  )}
                  <p className="text-xs text-blue-600 mt-1">{finding.suggestion}</p>
                  {finding.verdictConflict?.status === 'conflict-with-canonical-conclusion' && (
                    <div
                      className="text-xs mt-1.5 p-2 bg-warning/10 border-l-2 border-warning text-text-secondary flex flex-col gap-0.5"
                      data-testid={`sanity-finding-verdict-conflict-${finding.id}`}
                    >
                      <span className="font-bold text-warning">
                        {finding.verdictConflict.conflictNote}
                      </span>
                      <span>
                        <span className="font-semibold">Reconciled display state:</span>{' '}
                        {finding.verdictConflict.reconciledDisplaySummary}
                      </span>
                    </div>
                  )}
                  {!finding.resolution && (finding.correctionLineage?.status === 'applied-still-failing' || finding.correctionLineage?.status === 'superseded-by-evidence') && (
                    <div
                      className="text-xs mt-1.5 p-2 bg-warning/10 border-l-2 border-warning text-text-secondary flex flex-col gap-0.5"
                      data-testid={`sanity-finding-lineage-${finding.id}`}
                    >
                      <span className="font-bold text-warning">
                        {finding.correctionLineage.status === 'superseded-by-evidence'
                          ? 'Prior correction was superseded by updated source evidence on rerun:'
                          : 'Prior correction was applied and reevaluated on rerun, but this check still triggered:'}
                      </span>
                      {finding.correctionLineage.appliedDirective && (
                        <span>
                          <span className="font-semibold">Applied directive:</span> {finding.correctionLineage.appliedDirective}
                        </span>
                      )}
                      {finding.correctionLineage.supersededByEvidence && (
                        <span>
                          <span className="font-semibold">Updated evidence:</span> {finding.correctionLineage.supersededByEvidence}
                        </span>
                      )}
                      {finding.correctionLineage.remainingReason && (
                        <span>
                          <span className="font-semibold">Remaining reason:</span> {finding.correctionLineage.remainingReason}
                        </span>
                      )}
                    </div>
                  )}
                  {finding.resolution && (
                    <div className="text-xs mt-1 flex flex-col gap-0.5">
                      <span className={`font-bold ${
                        finding.resolution === 'apply' ? 'text-info' :
                        isVerifiedFinding(finding) ? 'text-success' :
                        'text-text-secondary'
                      }`}>
                        {finding.resolution === 'apply'
                          ? 'Will apply on rerun'
                          : isVerifiedFinding(finding)
                            ? 'Acknowledged'
                            : 'Ignored'}
                      </span>
                      {finding.resolution === 'apply' && (
                        <span className="text-text-secondary">— {queuedText}</span>
                      )}
                      {finding.resolution === 'ignore' && finding.userNote && finding.userNote.trim().length > 0 && (
                        <span
                          className="text-text-secondary"
                          data-testid={`sanity-finding-note-${finding.id}`}
                        >
                          — {finding.userNote}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {(!finding.resolution || (finding.resolution === 'apply' && !isVerifiedFinding(finding))) && (
                <div className="space-y-2 pt-1">
                  {isEditing && (
                    <div className="space-y-1.5">
                      {!isVerifiedFinding(finding) && candidateFields.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                          <span className="text-text-muted font-semibold">Target field:</span>
                          {candidateFields.map((cf) => {
                            const active = (fieldPathDrafts[finding.id]?.trim() || candidateFields[0]?.path) === cf.path;
                            return (
                              <button
                                key={cf.path}
                                type="button"
                                onClick={() => setFieldPathDrafts((fp) => ({ ...fp, [finding.id]: cf.path }))}
                                data-testid={`sanity-finding-fieldchip-${finding.id}-${cf.path}`}
                                className={`px-1.5 py-0.5 rounded border text-[11px] font-mono ${
                                  active
                                    ? 'border-primary bg-primary/15 text-primary font-semibold'
                                    : 'border-border text-text-secondary hover:text-text-primary'
                                }`}
                              >
                                {cf.path}
                              </button>
                            );
                          })}
                          {selectedCandidateField && (
                            <button
                              type="button"
                              onClick={() => {
                                setFieldPathDrafts((fp) => ({
                                  ...fp,
                                  [finding.id]: selectedCandidateField.path,
                                }));
                                setDrafts((d) => ({
                                  ...d,
                                  [finding.id]: selectedCandidateField.currentText,
                                }));
                              }}
                              data-testid={`sanity-finding-load-current-${finding.id}`}
                              className="ml-auto text-[11px] text-primary hover:underline"
                            >
                              Load current field text
                            </button>
                          )}
                        </div>
                      )}
                      <textarea
                        value={draft}
                        onChange={e => setDrafts(d => ({ ...d, [finding.id]: e.target.value }))}
                        rows={3}
                        className="w-full text-xs border border-border bg-card p-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                        data-testid={`sanity-finding-edit-${finding.id}`}
                        aria-label={`Edit suggestion for ${finding.section}`}
                      />
                      {!isVerifiedFinding(finding) && onApplyNow && (
                        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                          <input
                            type="text"
                            value={citationDrafts[finding.id] ?? finding.evidence ?? ''}
                            onChange={e => setCitationDrafts(c => ({ ...c, [finding.id]: e.target.value }))}
                            placeholder="Source citation (e.g. [Ref 1] or sed.grid.capacityMW)"
                            className="w-full text-xs border border-border bg-card px-2 py-1 text-text-primary"
                            data-testid={`sanity-finding-citation-${finding.id}`}
                            aria-label={`Source citation for ${finding.section}`}
                          />
                          <input
                            type="text"
                            value={fieldPathDrafts[finding.id] ?? ''}
                            onChange={e => setFieldPathDrafts(fp => ({ ...fp, [finding.id]: e.target.value }))}
                            placeholder={`Field path (default: ${selectedCandidateField?.path ?? finding.section})`}
                            className="w-full text-xs border border-border bg-card px-2 py-1 text-text-primary"
                            data-testid={`sanity-finding-fieldpath-${finding.id}`}
                            aria-label={`Target field path for ${finding.section}`}
                          />
                          <input
                            type="text"
                            value={targetSubstringDrafts[finding.id] ?? ''}
                            onChange={e => setTargetSubstringDrafts(ts => ({ ...ts, [finding.id]: e.target.value }))}
                            placeholder="Target substring (optional)"
                            className="w-full text-xs border border-border bg-card px-2 py-1 text-text-primary"
                            data-testid={`sanity-finding-substring-${finding.id}`}
                            aria-label={`Target substring for ${finding.section}`}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {/* ITT-2 — adaptive action set.
                       Verified findings (risk-verified / strength-verified)
                       are positive checks confirming the report's claims —
                       "Apply on rerun" makes no sense ("there is nothing to
                       apply"). Surface "Acknowledge" instead, which writes
                       the same `resolution: 'ignore'` downstream event so
                       revisit tracking is unchanged — only the framing is
                       different. "Edit suggestion" stays so the user can
                       still revise the AI's note before acknowledging. */}
                  {isVerifiedFinding(finding) ? (
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          const edited = isEditing && draft.trim().length > 0 ? draft.trim() : undefined;
                          onResolve(finding, idx, { resolution: 'ignore', userNote: edited });
                          setEditing(e => ({ ...e, [finding.id]: false }));
                          setDrafts(d => { const next = { ...d }; delete next[finding.id]; return next; });
                        }}
                        className="text-xs font-bold uppercase tracking-widest text-success hover:text-success/80 px-2 py-1 rounded-lg border border-success/40 hover:bg-success/10 flex items-center gap-1"
                        data-testid={`sanity-finding-acknowledge-${finding.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Acknowledge
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(e => ({ ...e, [finding.id]: !e[finding.id] }))}
                        className="type-meta text-text-muted hover:text-text-primary underline flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-1"
                        data-testid={`sanity-finding-edit-toggle-${finding.id}`}
                      >
                        <Pencil className="w-3 h-3" /> {isEditing ? 'Cancel edit' : 'Edit suggestion'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex gap-2 flex-wrap">
                        {onApplyNow && (
                          <button
                            type="button"
                            disabled={!!applyingNow[finding.id]}
                            onClick={async () => {
                              const edited = isEditing ? draft : undefined;
                              const replacementText = (
                                edited && edited.trim().length > 0 ? edited.trim() : queuedText
                              ).trim();
                              const complaintCheck = isDiagnosticComplaintNotReplacement(
                                replacementText,
                                [finding.finding ?? ''].filter(Boolean),
                              );
                              if (complaintCheck.isComplaint) {
                                setEditing(e => ({ ...e, [finding.id]: true }));
                                if (selectedCandidateField && !fieldPathDrafts[finding.id]) {
                                  setFieldPathDrafts(fp => ({
                                    ...fp,
                                    [finding.id]: selectedCandidateField.path,
                                  }));
                                }
                                setApplyNowErrors(prev => ({
                                  ...prev,
                                  [finding.id]:
                                    'Enter source-supported replacement narrative prose before applying (the audit complaint text itself cannot be written into the report).',
                                }));
                                return;
                              }
                              const evidenceCitation = (
                                citationDrafts[finding.id] ??
                                finding.evidence ??
                                '[Ref 1]'
                              ).trim();
                              const fieldPath =
                                fieldPathDrafts[finding.id]?.trim() ||
                                (candidateFields.length > 1 ? selectedCandidateField?.path : undefined);
                              const targetSubstring = targetSubstringDrafts[finding.id]?.trim() || undefined;
                              const directive = buildDirective(finding, edited ?? queuedText);
                              setApplyingNow(prev => ({ ...prev, [finding.id]: true }));
                              setApplyNowErrors(prev => {
                                const next = { ...prev };
                                delete next[finding.id];
                                return next;
                              });
                              try {
                                const res = await onApplyNow(finding, idx, {
                                  replacementText,
                                  evidenceCitation,
                                  fieldPath,
                                  targetSubstring,
                                  userNote: (edited && edited.trim().length > 0 ? edited.trim() : finding.userNote) || undefined,
                                  applyDirective: directive,
                                });
                                if (res && res.applied === false) {
                                  setApplyNowErrors(prev => ({
                                    ...prev,
                                    [finding.id]: res.error ?? res.reason ?? 'Correction could not be applied.',
                                  }));
                                } else {
                                  setEditing(e => ({ ...e, [finding.id]: false }));
                                  setDrafts(d => { const next = { ...d }; delete next[finding.id]; return next; });
                                }
                              } catch (err) {
                                setApplyNowErrors(prev => ({
                                  ...prev,
                                  [finding.id]: err instanceof Error ? err.message : 'Correction could not be applied.',
                                }));
                              } finally {
                                setApplyingNow(prev => ({ ...prev, [finding.id]: false }));
                              }
                            }}
                            className="text-xs font-bold uppercase tracking-widest text-primary hover:text-primary/80 px-2 py-1 border border-primary/40 hover:bg-primary/10 flex items-center gap-1 disabled:opacity-50"
                            data-testid={`sanity-finding-apply-now-${finding.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" /> {applyingNow[finding.id] ? 'Applying…' : 'Apply correction now'}
                          </button>
                        )}
                        {(!finding.resolution || isEditing) && (
                          <button
                            type="button"
                            onClick={() => {
                              const edited = isEditing ? draft : undefined;
                              const directive = buildDirective(finding, edited);
                              onResolve(finding, idx, {
                                resolution: 'apply',
                                userNote: edited && edited.trim().length > 0 ? edited.trim() : finding.userNote,
                                applyDirective: directive,
                              });
                              // Clear local edit state for this finding.
                              setEditing(e => ({ ...e, [finding.id]: false }));
                              setDrafts(d => { const next = { ...d }; delete next[finding.id]; return next; });
                            }}
                            className="text-xs font-bold uppercase tracking-widest text-info hover:text-info/80 px-2 py-1 border border-info/40 hover:bg-info/10 flex items-center gap-1"
                            data-testid={`sanity-finding-apply-${finding.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" /> {finding.resolution === 'apply' ? 'Update queued edit' : 'Apply on rerun'}
                          </button>
                        )}
                        {!finding.resolution && (
                          <button
                            type="button"
                            onClick={() => onResolve(finding, idx, { resolution: 'ignore' })}
                            className="text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                            data-testid={`sanity-finding-ignore-${finding.id}`}
                          >
                            <XCircle className="w-3 h-3" /> Ignore
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (!isEditing && selectedCandidateField && !fieldPathDrafts[finding.id]) {
                              setFieldPathDrafts(fp => ({
                                ...fp,
                                [finding.id]: selectedCandidateField.path,
                              }));
                            }
                            setEditing(e => ({ ...e, [finding.id]: !e[finding.id] }));
                          }}
                          className="type-meta text-text-muted hover:text-text-primary underline flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-1"
                          data-testid={`sanity-finding-edit-toggle-${finding.id}`}
                        >
                          <Pencil className="w-3 h-3" /> {isEditing ? 'Cancel edit' : 'Edit suggestion'}
                        </button>
                      </div>
                      {applyNowErrors[finding.id] && (
                        <p
                          className="text-xs text-danger"
                          role="alert"
                          data-testid={`sanity-finding-apply-error-${finding.id}`}
                        >
                          {applyNowErrors[finding.id]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {finding.resolution && (
                <button
                  type="button"
                  onClick={() => onResolve(finding, idx, { resolution: undefined })}
                  className="text-xs text-text-secondary hover:text-text-secondary underline"
                  data-testid={`sanity-finding-undo-${finding.id}`}
                >
                  Undo
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
